import React, { useMemo, useState } from 'react';
import { ClipboardCheck, Send } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { ConfirmationDialog } from '@roger/ui-components';
import {
  getActiveBillingCycles,
  sendBillingCycleToClearinghouse,
  setBillingCycleStatusPendingPayment,
  setBillingCycleStatusVoid,
} from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { claimAgeDays } from '../claimAge';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';
import { PermissionSlug } from '../../../constants/featureFlags';
import { useToast } from '../../../hooks/useToastContext';
import { useStore } from '../../../store';
import BillingPeriodCell from '../BillingPeriodCell';
import { BillingDataTable } from '../BillingDataTable';
import { BillingSelectionActionBar } from '../BillingSelectionActionBar';
import { activeBillingCycleNoteContext } from '../billingCycleNoteContext';
import { currencySortingFn } from '../billingTableUtils';
import { showVoidBillingCycleFailures } from '../showVoidBillingCycleFailures';
import { showSendBillingCycleFailures } from '../showSendBillingCycleFailures';
import { PatientCell } from '../PatientCell';
import { InactiveEpisodeBadge } from '../InactiveEpisodeBadge';
import { ImportedPatientBadge } from '../../patient/ImportedPatientBadge';
import { TestPatientBadge } from '../../patient/TestPatientBadge';
import { TestPatientSubmitConfirmDialog } from '../../TestPatientSubmitConfirmDialog';
import { InsuranceCell } from '../InsuranceCell';
import { BillingActionsCell } from '../BillingActionsCell';
import { ClaimWorksheetViewerAction } from '../ClaimWorksheetViewerAction';
import { ReplacementAction } from '../ReplacementAction';
import {
  VoidClaimsConfirmDialog,
  type VoidCopyOptions,
} from '../VoidClaimsButton';

interface EOEReadyToSendTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEReadyToSendTab: React.FC<EOEReadyToSendTabProps> = ({
  onCountsInvalidated,
}) => {
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [testPatientAckOpen, setTestPatientAckOpen] = useState(false);
  const [testPatientCount, setTestPatientCount] = useState(0);
  const [markSentConfirmOpen, setMarkSentConfirmOpen] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<
    'send' | 'markSent' | 'void' | null
  >(null);
  const { error: showError } = useToast();
  const canSubmitClaims = useStore(state =>
    state.hasPermission(PermissionSlug.CLAIMS_SUBMIT)
  );

  const query = useBillingTableQuery('ready-to-send', {
    sortKeyByColumnId: { billing_period: 'billing_period_start' },
  });
  const {
    data,
    isLoading,
    isRefreshing,
    selectedIds,
    selectedItems,
    total,
    totalPages,
    clearSelection,
    removeSelectedId,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
    loadData,
  } = useBillingCycles<
    ActiveBillingCycleItem,
    { statuses: EpisodicClaimStatus[] }
  >({
    tabId: 'ready-to-send',
    fetchFn: getActiveBillingCycles,
    fetchPayload: { statuses: ['READY_TO_SEND'], ...query.toParams() },
    errorMessage: 'Could not fetch data for the "Ready to Send" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
    getMrn: item => item.patient_mrn,
  });

  const doSend = (acknowledge: boolean) => {
    setSubmittingAction('send');
    return mutateSelected(
      id =>
        sendBillingCycleToClearinghouse({
          path: { billing_cycle_id: id },
          query: acknowledge ? { acknowledge_test_patient: true } : undefined,
        }),
      'Some billing cycles failed to send',
      'The following billing cycles could not be updated',
      onCountsInvalidated,
      failures => showSendBillingCycleFailures(failures, showError)
    ).finally(() => setSubmittingAction(null));
  };

  const handleConfirmSend = () => {
    setSendConfirmOpen(false);
    // Real claims to the clearinghouse for test patients warn-and-confirm
    // (ROG-2900). Rows already carry is_test_patient, so we can check up front.
    const testCount = data.filter(
      item => selectedIds.has(item.billing_cycle_id) && item.is_test_patient
    ).length;
    if (testCount > 0) {
      setTestPatientCount(testCount);
      setTestPatientAckOpen(true);
      return;
    }
    void doSend(false);
  };

  const handleConfirmTestPatientSend = () => {
    setTestPatientAckOpen(false);
    void doSend(true);
  };

  const handleVoid = (options: VoidCopyOptions) => {
    setSubmittingAction('void');
    void mutateSelected(
      id =>
        setBillingCycleStatusVoid({
          path: { billing_cycle_id: id },
          body: {
            copy_worksheet_forward: options.copyWorksheetForward,
            copy_notes_forward: options.copyNotesForward,
          },
        }),
      'Some billing cycles failed to void',
      'The following billing cycles could not be updated',
      onCountsInvalidated,
      failures => showVoidBillingCycleFailures(failures, showError)
    ).finally(() => setSubmittingAction(null));
  };

  const handleConfirmMarkSent = () => {
    setMarkSentConfirmOpen(false);
    setSubmittingAction('markSent');
    void mutateSelected(
      id =>
        setBillingCycleStatusPendingPayment({
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles could not be marked as sent',
      'The following billing cycles could not be updated',
      onCountsInvalidated
    ).finally(() => setSubmittingAction(null));
  };

  const columns = useMemo<ColumnDef<ActiveBillingCycleItem>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <div className='flex flex-col items-start gap-1'>
            <PatientCell
              name={row.original.patient_name}
              mrn={row.original.patient_mrn}
              patientId={row.original.patient_id}
            />
            {row.original.is_test_patient && (
              <TestPatientBadge className='shrink-0' />
            )}
            {row.original.show_imported_badge && (
              <ImportedPatientBadge
                importedAt={row.original.imported_at}
                className='shrink-0'
              />
            )}
            {row.original.episode_status === 'cancelled' && (
              <InactiveEpisodeBadge className='self-start' />
            )}
          </div>
        ),
      },
      {
        id: 'branch',
        accessorKey: 'branch',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.billing_period_start,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.billing_period_start)}
            endDate={formatDateToMMDDYYYY(row.original.billing_period_end)}
          />
        ),
      },
      {
        id: 'claim_number',
        accessorKey: 'claim_number',
        header: 'Claim #',
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>{row.original.claim_number}</div>
            {row.original.claim_version_label ? (
              <div className='text-xs text-muted-foreground'>
                {row.original.claim_version_label}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'tob',
        accessorKey: 'tob',
        header: 'TOB',
        enableSorting: false,
      },
      {
        id: 'total_charges',
        accessorKey: 'total_charges',
        header: 'Total Charges',
        enableSorting: false,
        sortingFn: currencySortingFn,
      },
      {
        id: 'claim_age',
        header: 'Claim Age',
        accessorFn: row => claimAgeDays(row.billing_period_end) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            periodEnd={row.original.billing_period_end}
            createdAt={row.original.created_at}
            billedAt={row.original.billed_at}
          />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            noteProps={{
              ...activeBillingCycleNoteContext(row.original),
              patientName: row.original.patient_name,
            }}
            leading={
              <>
                {(row.original.claim_correction_kind === 'replacement' ||
                  row.original.claim_correction_kind === 'resubmission') && (
                  <ReplacementAction
                    billingCycleId={row.original.billing_cycle_id}
                    billingCycleStatus={row.original.billing_cycle_status}
                    claimCorrectionKind={row.original.claim_correction_kind}
                    onDone={async () => {
                      await loadData();
                      await onCountsInvalidated?.();
                    }}
                  />
                )}
                <ClaimWorksheetViewerAction billingCycle={row.original} />
              </>
            }
          />
        ),
      },
    ],
    [loadData, onCountsInvalidated]
  );

  const isAnyActionSubmitting = submittingAction !== null;

  return (
    <div className='flex flex-col gap-4'>
      <BillingDataTable
        data={data}
        columns={columns}
        getRowId={row => row.billing_cycle_id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        selection={{
          selectedIds,
          onToggleId: handleToggleId,
          onToggleAllVisible: handleToggleAll,
        }}
      />
      <ConfirmationDialog
        open={sendConfirmOpen}
        title='Send claims?'
        message='This will send the claim to the clearinghouse and cannot be undone.'
        confirmLabel='Send'
        onAccept={handleConfirmSend}
        onCancel={() => setSendConfirmOpen(false)}
      />
      <TestPatientSubmitConfirmDialog
        open={testPatientAckOpen}
        actionLabel='claim'
        count={testPatientCount}
        loading={submittingAction === 'send'}
        onConfirm={handleConfirmTestPatientSend}
        onCancel={() => setTestPatientAckOpen(false)}
      />
      <ConfirmationDialog
        open={markSentConfirmOpen}
        title='Mark claims as sent?'
        message="Use this only if you already submitted the claim yourself. Roger moves it to Pending Payment but won't send or track it — you'll mark it Paid or Rejected later. This can't be undone."
        confirmLabel='Mark as Sent'
        onAccept={handleConfirmMarkSent}
        onCancel={() => setMarkSentConfirmOpen(false)}
      />
      <VoidClaimsConfirmDialog
        open={voidConfirmOpen}
        selectedCount={selectedIds.size}
        postSend={false}
        onConfirm={options => {
          setVoidConfirmOpen(false);
          handleVoid(options);
        }}
        onCancel={() => setVoidConfirmOpen(false)}
      />
      <BillingSelectionActionBar
        selectedItems={selectedItems}
        onClear={clearSelection}
        onRemoveSelectedId={removeSelectedId}
        actions={[
          ...(canSubmitClaims
            ? [
                {
                  key: 'send',
                  label: 'Send',
                  icon: Send,
                  disabledReason: () =>
                    isAnyActionSubmitting || isLoading
                      ? 'Action in progress'
                      : undefined,
                  run: () => setSendConfirmOpen(true),
                },
                {
                  key: 'mark-sent',
                  label: 'Mark as Sent',
                  icon: ClipboardCheck,
                  disabledReason: () =>
                    isAnyActionSubmitting || isLoading
                      ? 'Action in progress'
                      : undefined,
                  run: () => setMarkSentConfirmOpen(true),
                },
              ]
            : []),
          {
            key: 'void',
            label: 'Void',
            destructive: true,
            disabledReason: () =>
              isAnyActionSubmitting || isLoading
                ? 'Action in progress'
                : undefined,
            run: () => setVoidConfirmOpen(true),
          },
        ]}
      />
    </div>
  );
};

export default EOEReadyToSendTab;
